'use client'

import { useRef } from 'react'
import classnames from 'classnames'
import styles from './AllergenTable.module.scss'
import { getAllergenCategoryId } from '@/utils/urls/slugs'
import { getCellState } from './get-cell-state'
import StickyHeaderBar from './StickyHeaderBar'
import StickyCategoryBand from './StickyCategoryBand'

export type AllergenTableColumn = { key: AllergenKey; label: string }

export type AllergenTableLabels = {
  caption: string
  itemColumn: string
  contains: string
  modifiable: string
}

type AllergenTableProps = {
  className?: string
  categories: SanityAllergenGuideCategory[]
  columns: AllergenTableColumn[]
  labels: AllergenTableLabels
}

const AllergenTable = ({ className, categories, columns, labels }: AllergenTableProps) => {
  const categoriesWithItems = categories.filter(category => category.items?.length)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const scrollerRef = useRef<HTMLDivElement | null>(null)

  return (
    <div
      ref={rootRef}
      className={classnames(styles.AllergenTable, className)}
    >
      <StickyHeaderBar
        columns={columns}
        rootRef={rootRef}
        scrollerRef={scrollerRef}
      />
      <StickyCategoryBand
        titles={categoriesWithItems.map(category => category.categoryReference.title)}
        rootRef={rootRef}
      />
      <div
        ref={scrollerRef}
        className={styles.scroller}
        data-lenis-prevent
      >
    <table className={styles.table}>
      <caption className="sr-only">{labels.caption}</caption>
      <colgroup>
        <col className={styles.nameColumn} />
        {columns.map(column => (
          <col
            key={column.key}
            className={styles.allergenColumn}
          />
        ))}
      </colgroup>
      <thead className={styles.thead}>
        <tr>
          <th
            scope="col"
            className={styles.corner}
          >
            <span className="sr-only">{labels.itemColumn}</span>
          </th>
          {columns.map(column => (
            <th
              key={column.key}
              scope="col"
              className={styles.columnHeader}
            >
              {column.label}
            </th>
          ))}
        </tr>
      </thead>
      {categoriesWithItems.map(category => (
        <tbody
          key={category.categoryReference.slug.current}
          id={getAllergenCategoryId(category.categoryReference.slug.current)}
          className={styles.category}
          data-allergen-category={category.categoryReference.slug.current}
        >
          <tr>
            <th
              scope="rowgroup"
              colSpan={columns.length + 1}
              className={styles.band}
            >
              <span className={styles.bandText}>{category.categoryReference.title}</span>
            </th>
          </tr>
          {category.items.map(item => (
            <tr key={item._id}>
              <th
                scope="row"
                className={styles.itemName}
              >
                {item.title}
              </th>
              {columns.map(column => {
                const state = getCellState(item, column.key)
                return (
                  <td
                    key={column.key}
                    className={styles.cell}
                  >
                    {state === 'contains' && (
                      <>
                        <span aria-hidden="true">x</span>
                        <span className="sr-only">{labels.contains}</span>
                      </>
                    )}
                    {state === 'modifiable' && (
                      <>
                        <span aria-hidden="true">(x)</span>
                        <span className="sr-only">{labels.modifiable}</span>
                      </>
                    )}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      ))}
    </table>
      </div>
    </div>
  )
}

AllergenTable.displayName = 'AllergenTable'

export default AllergenTable
